import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { MODULES, MODULE_ICONS } from '../data/modules';

export default function SiteNav() {
  const [open, setOpen] = useState(false);
  const [modulesOpen, setModulesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isAuthenticated, isAdmin } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const accountLink = isAuthenticated && isAdmin ? '/admin' : '/login';
  const accountLabel = isAuthenticated && isAdmin ? 'Admin Dashboard' : 'Sign In';
  const liveModules = MODULES.filter((m) => m.status === 'Live');
  const comingModules = MODULES.filter((m) => m.status !== 'Live');

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-surface1/90 backdrop-blur-md border-b border-border' : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2 font-display">
            <img src="/logo.png" alt="Bankably" className="h-8 w-8" />
            <span className="text-2xl font-bold text-textPrimary">Bankably</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <div className="relative" onMouseEnter={() => setModulesOpen(true)} onMouseLeave={() => setModulesOpen(false)}>
              <button className="flex items-center gap-1 text-textSecondary hover:text-textPrimary font-medium transition">
                Modules <ChevronDown size={14} className={`transition-transform ${modulesOpen ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {modulesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[36rem] bg-surface1 border border-border rounded-2xl shadow-2xl p-6 grid grid-cols-2 gap-6"
                  >
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-success mb-3">Live Now</p>
                      <div className="space-y-1">
                        {liveModules.map((m) => {
                          const Icon = MODULE_ICONS[m.icon] ?? MODULE_ICONS.Wallet;
                          return (
                            <Link
                              key={m.slug}
                              to={`/modules/${m.slug}`}
                              onClick={() => setModulesOpen(false)}
                              className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-surface2 text-sm text-textSecondary hover:text-textPrimary transition"
                            >
                              <Icon size={15} className="text-gold flex-shrink-0" /> {m.shortName}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-textTertiary mb-3">Coming Soon</p>
                      <div className="space-y-1">
                        {comingModules.map((m) => {
                          const Icon = MODULE_ICONS[m.icon] ?? MODULE_ICONS.Wallet;
                          return (
                            <Link
                              key={m.slug}
                              to={`/modules/${m.slug}`}
                              onClick={() => setModulesOpen(false)}
                              className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-surface2 text-sm text-textSecondary hover:text-textPrimary transition"
                            >
                              <Icon size={15} className="text-textTertiary flex-shrink-0" /> {m.shortName}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <Link to="/wishlist" className="text-textSecondary hover:text-textPrimary font-medium transition">Join Waitlist</Link>
            <Link to="/proof-layer" className="text-textSecondary hover:text-textPrimary font-medium transition">The Proof Layer</Link>
            <Link
              to={accountLink}
              className="px-6 py-2 bg-gold-gradient text-bg rounded-lg font-semibold hover:opacity-90 transition"
            >
              {accountLabel}
            </Link>
          </div>

          <button onClick={() => setOpen(!open)} className="md:hidden text-textPrimary" aria-label="Toggle menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-surface1 border-b border-border overflow-hidden"
          >
            <div className="px-4 py-4 space-y-3">
              <Link to="/#modules" className="block text-textSecondary hover:text-textPrimary font-medium py-2" onClick={() => setOpen(false)}>Modules</Link>
              <Link to="/wishlist" className="block text-textSecondary hover:text-textPrimary font-medium py-2" onClick={() => setOpen(false)}>Join Waitlist</Link>
              <Link to="/proof-layer" className="block text-textSecondary hover:text-textPrimary font-medium py-2" onClick={() => setOpen(false)}>The Proof Layer</Link>
              <Link
                to={accountLink}
                className="block w-full px-6 py-2 bg-gold-gradient text-bg rounded-lg font-semibold text-center"
                onClick={() => setOpen(false)}
              >
                {accountLabel}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
